import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import Backend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { isExtensionContext, isInPopup, isWebPageButNotWhatsappPage } from '@common/services/detect-context';
import { criticalTranslations } from './critical-translations';
import { applyDocumentDirection } from '@common/services/rtl';
import { applyMomentLocale } from './moment-locales';
import { isClearFlow } from '@common/utils/clear-flow';
import { CLEAR_BRAND_NAME, DEFAULT_BRAND_NAME } from './clear-brand';

let i18nInitialized = false;
const SUPPORTED_LANGS = ['en', 'pt', 'es', 'de', 'it', 'fr', 'hi', 'he', 'ar'] as const;

const toBaseLanguage = (language: string | null | undefined): string => {
  if (!language) return 'en';
  return language.split('-')[0].toLowerCase();
};

/**
 * Rebrands translated copy inside the chromeless "clear" flow.
 *
 * ~40 strings per locale mention "Blueticks" by name (the backup page's
 * "back up to Blueticks servers", the connections page's offline-gateway copy,
 * and so on). The clear flow is presented as its own product, so none of them may
 * surface there. Doing this as a post-processor rebrands every string in every
 * language from one place, instead of forking 10 translation files.
 *
 * Outside the clear flow this is a no-op, so the regular product is untouched.
 */
const brandPostProcessor = {
  type: 'postProcessor' as const,
  name: 'clearBrand',
  process(value: string): string {
    if (typeof value !== 'string' || !value.includes(DEFAULT_BRAND_NAME)) return value;
    if (!isClearFlow()) return value;
    return value.split(DEFAULT_BRAND_NAME).join(CLEAR_BRAND_NAME);
  },
};

const initializeI18n = () => {
  if (i18nInitialized) {
    return i18n;
  }

  // Only initialize in appropriate contexts
  const shouldInitialize = isExtensionContext() || isWebPageButNotWhatsappPage() || isInPopup();
  
  if (!shouldInitialize) {
    return i18n;
  }

  // Custom loadPath function that handles both extension and website contexts
  const getLoadPath = (lng: any, ns: any): string => {
    try {
      // Map language codes to available locales
      // Map en-US, en-GB, etc. to 'en'
      let langCode = 'en'; // default fallback
      
      if (typeof lng === 'string') {
        langCode = lng;
      } else if (Array.isArray(lng) && lng.length > 0 && typeof lng[0] === 'string') {
        // Sometimes i18next passes an array of languages
        langCode = lng[0];
      } else if (lng && typeof lng === 'object' && typeof lng.toString === 'function') {
        // Handle other object types that might have a string representation
        langCode = lng.toString();
      }
      
      // Map language codes to available locale directories
      // Languages without a locale directory MUST fall back to 'en',
      // otherwise the SPA catch-all returns index.html (200 + HTML)
      // which i18next silently fails to parse, breaking all translations.
      let mappedLng = 'en'; // default to English for unmapped languages
      if (langCode.startsWith('en')) {
        mappedLng = 'en';
      } else if (langCode.startsWith('pt')) {
        mappedLng = 'pt';
      } else if (langCode.startsWith('es')) {
        mappedLng = 'es-ES';
      } else if (langCode.startsWith('de')) {
        mappedLng = 'de-DE';
      } else if (langCode.startsWith('it')) {
        mappedLng = 'it-IT';
      } else if (langCode.startsWith('hi')) {
        mappedLng = 'hi-IN';
      } else if (langCode.startsWith('fr')) {
        mappedLng = 'fr-FR';
      } else if (langCode.startsWith('he')) {
        mappedLng = 'he-IL';
      } else if (langCode.startsWith('ar')) {
        mappedLng = 'ar-SA';
      }
      
      // Ensure namespace is also a string
      const namespace = typeof ns === 'string' ? ns : 'common';
      
      // Check if chrome runtime is available (extension context only)
      if (typeof chrome !== 'undefined' && chrome.runtime?.getURL) {
        return chrome.runtime.getURL(`locales/${mappedLng}/${namespace}.json`);
      }
      
      // Fallback for web context - use relative path
      return `/locales/${mappedLng}/${namespace}.json`;
    } catch (error) {
      console.error('Error in i18n loadPath:', error, 'lng:', lng, 'ns:', ns);
      // Fallback to default English locale
      const fallbackNamespace = typeof ns === 'string' ? ns : 'common';
      const fallbackPath = `locales/en/${fallbackNamespace}.json`;
      if (typeof chrome !== 'undefined' && chrome.runtime?.getURL) {
        return chrome.runtime.getURL(fallbackPath);
      }
      return `/locales/en/${fallbackNamespace}.json`;
    }
  };

  i18n
    .use(Backend)
    .use(LanguageDetector)
    .use(brandPostProcessor)
    .use(initReactI18next)
    .init({
      // Rebrands "Blueticks" -> the clear-flow product name; no-op elsewhere.
      postProcess: ['clearBrand'],
      fallbackLng: 'en',
      supportedLngs: [...SUPPORTED_LANGS],
      nonExplicitSupportedLngs: true,
      // Add language mapping to handle en-US -> en
      load: 'languageOnly', // This will load 'en' for 'en-US'
      debug: process.env.NODE_ENV === 'development',
      ns: ['common'],
      defaultNS: 'common',
      // NOTE: Do NOT use `resources` + `partialBundledLanguages` here.
      // That combination prevents the HTTP backend from loading the full
      // translation bundle for languages that already have partial resources,
      // breaking translations for those languages entirely.
      backend: {
        loadPath: getLoadPath,
      },
      interpolation: {
        escapeValue: false, // not needed for react as it escapes by default
      },
      react: {
        useSuspense: false, // Disable suspense for SSR compatibility
      },
      detection: {
        // Language detection options
        order: ['localStorage', 'navigator', 'htmlTag'],
        caches: ['localStorage'],
        lookupLocalStorage: 'i18nextLng',
      },
      // Handle missing translations gracefully
      saveMissing: process.env.NODE_ENV === 'development',
      missingKeyHandler: (lngs: readonly string[], ns: string, key: string) => {
        // Most "missing key" hits are the initial render racing the async
        // HTTP bundle load (keys resolve on the post-load re-render), so this
        // is noise unless actively debugging translations — gate it like the
        // other BT_DEBUG diagnostics.
        if (process.env.NODE_ENV === 'development'
          && typeof localStorage !== 'undefined' && localStorage.getItem('BT_DEBUG') === '1') {
          console.warn(`[i18next] Missing translation key: ${key} in namespace: ${ns} for language: ${lngs.join(', ')}`);
        }
        // Return the key itself as fallback (handler signature expects void)
      },
      // Return empty string for missing keys in production to avoid showing keys
      returnEmptyString: false,
      returnNull: false,
    })
    .then(async () => {
      // Deep-merge critical signup/login translations as a safety net.
      // IMPORTANT: only seed the ACTIVE language to avoid creating partial
      // bundles for inactive languages (which can block live backend loading
      // when switching later, e.g. pt -> fallback to en keys).
      const activeBaseLanguage = toBaseLanguage(i18n.resolvedLanguage || i18n.language);
      const activeCritical = criticalTranslations[activeBaseLanguage];
      if (activeCritical) {
        for (const [ns, translations] of Object.entries(activeCritical as Record<string, any>)) {
          i18n.addResourceBundle(activeBaseLanguage, ns, translations, true, false);
        }
      }

      if (process.env.NODE_ENV === 'development') {
        const resourceBundle = i18n.getResourceBundle(i18n.language, 'common');
        console.log('[i18next] Initialized:', {
          language: i18n.language,
          languages: i18n.languages,
          isInitialized: i18n.isInitialized,
          context: isExtensionContext() ? 'extension' : 'website',
          bundleKeys: resourceBundle ? Object.keys(resourceBundle).length : 0,
        });
      }

      // Keep layout direction synchronized with active language.
      applyDocumentDirection(i18n.language);
      applyMomentLocale(i18n.language);
      i18n.on('languageChanged', (language: string) => {
        applyDocumentDirection(language);
        applyMomentLocale(language);
      });

      try {
        const { applyExtensionWhatsappLanguage } = await import(/* webpackMode: "eager" */ 
          '@common/services/language-detection'
        );
        await applyExtensionWhatsappLanguage();
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            '[i18next] Extension WhatsApp language override failed:',
            error,
          );
        }
      }
    })
    .catch((error) => {
      console.error('[i18next] Initialization error:', error);
    });

  i18nInitialized = true;
  return i18n;
};

// Initialize immediately
initializeI18n();

export default i18n;